import React, {useEffect, useState} from 'react';
import {
    Container,
    Typography,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Box,
    Link,
    CardContent,
} from '@mui/material';
import {
    DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material';
import {DragDropContext, Droppable, Draggable, DropResult} from 'react-beautiful-dnd';
import Header from "../../../ui/Header";
import {
    assignCommissionProduct,
    AssignCommissionProductRequest,
    CommissionCategory,
    CommissionProduct,
    fetchCommissionCategories,
    fetchCommissionProducts,
    unassignCommissionProduct
} from "../../../api/custom/CommissionApi";
import {Card} from "../../../components/Card";
import Spinner from '../../../ui/Spinner';


const CommissionManager: React.FC = () => {

    const [commissionCategories, setCommissionCategories] = useState<CommissionCategory[]>();
    const [unassignedCommissionProducts, setUnassignedCommissionProducts] = useState<CommissionProduct[]>();

    const [loadingCommissionCategories, setLoadingCommissionCategories] = useState<boolean>(true);
    const [loadingUnassignedCommissionProducts, setLoadingUnassignedCommissionProducts] = useState<boolean>(true)

    const [selectedCommissionCategory, setSelectedCommissionCategory] = useState<CommissionCategory | null>();

    useEffect(() => {
        loadCommissionCategories();
        loadUnassignedCommissionProducts();
    }, []);

    const loadCommissionCategories = async () => {
        setLoadingCommissionCategories(true);
        const response = await fetchCommissionCategories()
        setCommissionCategories(response)
        setLoadingCommissionCategories(false)
    }

    const loadUnassignedCommissionProducts = async () => {
        setLoadingUnassignedCommissionProducts(true);
        const response = await fetchCommissionProducts()
        setUnassignedCommissionProducts(response)
        setLoadingUnassignedCommissionProducts(false)
    }

    const handleCommissionCategorySelect = (id: number) => {
        const commissionCategory = commissionCategories?.find(
            category => category.id === id);

        setSelectedCommissionCategory(commissionCategory);
    };

    const handleDragEnd = async (result: DropResult) => {
        const {source, destination, draggableId} = result;

        // If dropped outside a valid droppable area
        if (!destination) {
            return;
        }

        // If dropped in the same position
        if (source.droppableId === destination.droppableId && source.index === destination.index) {
            return;
        }

        const productId = parseInt(draggableId.replace('product-', ''));

        try {
            // Handle drop on category from table
            if (destination.droppableId.startsWith('category-')) {
                const categoryId = parseInt(destination.droppableId.replace('category-', ''));

                // If coming from assigned products, we need to unassign first, then assign to new category
                if (source.droppableId === 'assigned' && selectedCommissionCategory) {
                    await handleReassignProduct(productId, selectedCommissionCategory.id, categoryId);
                } else if (source.droppableId === 'unassigned') {
                    await handleDropOnCategory(productId, categoryId);
                }
                return;
            }

            // Handle drop on assigned products section
            if (destination.droppableId === 'assigned' && source.droppableId === 'unassigned') {
                if (!selectedCommissionCategory) return;
                await handleDropOnCategory(productId, selectedCommissionCategory.id);
                return;
            }

            // Handle drop back to unassigned - unassign product from category
            if (destination.droppableId === 'unassigned' && source.droppableId === 'assigned') {
                if (!selectedCommissionCategory) return;
                await handleUnassignProduct(productId, selectedCommissionCategory.id);
                return;
            }

            // Handle reordering within assigned products
            if (source.droppableId === 'assigned' && destination.droppableId === 'assigned') {
                if (!selectedCommissionCategory) return;

                const reorderedProducts = Array.from(selectedCommissionCategory.commissionProducts);
                const [movedProduct] = reorderedProducts.splice(source.index, 1);
                reorderedProducts.splice(destination.index, 0, movedProduct);

                setCommissionCategories(prev =>
                    prev?.map(category =>
                        category.id === selectedCommissionCategory.id
                            ? {...category, commissionProducts: reorderedProducts}
                            : category
                    ) || []
                );

                setSelectedCommissionCategory(prev =>
                    prev ? {...prev, commissionProducts: reorderedProducts} : null
                );
            }

            // Handle reordering within unassigned products
            if (source.droppableId === 'unassigned' && destination.droppableId === 'unassigned') {
                if (!unassignedCommissionProducts) return;

                const reorderedProducts = Array.from(unassignedCommissionProducts);
                const [movedProduct] = reorderedProducts.splice(source.index, 1);
                reorderedProducts.splice(destination.index, 0, movedProduct);

                setUnassignedCommissionProducts(reorderedProducts);
            }

        } catch (error) {
            console.error('Error handling drag end:', error);
            // Reload data on error to maintain consistency
            loadCommissionCategories();
            loadUnassignedCommissionProducts();
        }
    };

    const handleDropOnCategory = async (productId: number, categoryId: number) => {
        try {
            const productToMove = unassignedCommissionProducts?.find(p => p.id === productId);
            if (!productToMove) {
                console.error('Product not found in unassigned products');
                return;
            }

            // Optimistically update UI
            setUnassignedCommissionProducts(prev =>
                prev?.filter(p => p.id !== productId) || []
            );

            setCommissionCategories(prev =>
                prev?.map(category =>
                    category.id === categoryId
                        ? {
                            ...category,
                            commissionProducts: [...category.commissionProducts, productToMove]
                        }
                        : category
                ) || []
            );

            if (selectedCommissionCategory?.id === categoryId) {
                setSelectedCommissionCategory(prev =>
                    prev ? {
                        ...prev,
                        commissionProducts: [...prev.commissionProducts, productToMove]
                    } : null
                );
            }

            // Make API call
            const request: AssignCommissionProductRequest = {
                commissionProductId: productId
            };

            await assignCommissionProduct(categoryId, request);

        } catch (error) {
            console.error('Error assigning product to category:', error);
            // Reload data on error
            loadCommissionCategories();
            loadUnassignedCommissionProducts();
        }
    };

    const handleUnassignProduct = async (productId: number, categoryId: number) => {
        try {
            // Find the product in the selected category
            const productToMove = selectedCommissionCategory?.commissionProducts.find(p => p.id === productId);
            if (!productToMove) {
                console.error('Product not found in assigned products');
                return;
            }

            // Optimistically update UI - remove from category
            setCommissionCategories(prev =>
                prev?.map(category =>
                    category.id === categoryId
                        ? {
                            ...category,
                            commissionProducts: category.commissionProducts.filter(p => p.id !== productId)
                        }
                        : category
                ) || []
            );

            // Update selected category if it's the one being modified
            if (selectedCommissionCategory?.id === categoryId) {
                setSelectedCommissionCategory(prev =>
                    prev ? {
                        ...prev,
                        commissionProducts: prev.commissionProducts.filter(p => p.id !== productId)
                    } : null
                );
            }

            // Add back to unassigned products
            setUnassignedCommissionProducts(prev =>
                prev ? [...prev, productToMove] : [productToMove]
            );

            await unassignCommissionProduct(categoryId, productId);

        } catch (error) {
            console.error('Error unassigning product from category:', error);
            // Reload data on error to maintain consistency
            loadCommissionCategories();
            loadUnassignedCommissionProducts();
        }
    };

    const handleReassignProduct = async (productId: number, fromCategoryId: number, toCategoryId: number) => {
        try {
            // Find the product in the source category
            const productToMove = selectedCommissionCategory?.commissionProducts.find(p => p.id === productId);
            if (!productToMove) {
                console.error('Product not found in source category');
                return;
            }

            // Optimistically update UI - remove from old category and add to new category
            setCommissionCategories(prev =>
                prev?.map(category => {
                    if (category.id === fromCategoryId) {
                        return {
                            ...category,
                            commissionProducts: category.commissionProducts.filter(p => p.id !== productId)
                        };
                    } else if (category.id === toCategoryId) {
                        return {
                            ...category,
                            commissionProducts: [...category.commissionProducts, productToMove]
                        };
                    }
                    return category;
                }) || []
            );

            // Update selected category if it's one of the affected categories
            if (selectedCommissionCategory?.id === fromCategoryId) {
                setSelectedCommissionCategory(prev =>
                    prev ? {
                        ...prev,
                        commissionProducts: prev.commissionProducts.filter(p => p.id !== productId)
                    } : null
                );
            } else if (selectedCommissionCategory?.id === toCategoryId) {
                setSelectedCommissionCategory(prev =>
                    prev ? {
                        ...prev,
                        commissionProducts: [...prev.commissionProducts, productToMove]
                    } : null
                );
            }

            // Make API calls - unassign from old, assign to new
            await unassignCommissionProduct(fromCategoryId, productId);

            const request: AssignCommissionProductRequest = {
                commissionProductId: productId
            };
            await assignCommissionProduct(toCategoryId, request);

        } catch (error) {
            console.error('Error reassigning product between categories:', error);
            // Reload data on error to maintain consistency
            loadCommissionCategories();
            loadUnassignedCommissionProducts();
        }
    };

    interface CommissionProductCardProps {
        commissionProduct: CommissionProduct;
        index: number;
    }

    const CommissionProductCard: React.FC<CommissionProductCardProps> = React.memo(({
                                                                                        commissionProduct,
                                                                                        index
                                                                                    }) => {
        return (
            <Draggable draggableId={`product-${commissionProduct.id}`} index={index}>
                {(provided, snapshot) => (
                    <Box
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        sx={{
                            mb: 1,
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                            backgroundColor: snapshot.isDragging ? 'action.hover' : 'background.paper',
                            '&:hover': {
                                backgroundColor: 'action.hover'
                            },
                            transition: 'background-color 0.2s ease'
                        }}
                    >
                        <Box sx={{
                            py: 1.5,
                            px: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            width: '100%',
                            userSelect: 'none'
                        }}>
                            <DragIndicatorIcon sx={{color: 'text.secondary', fontSize: 18}}/>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                                flex: 1
                            }}>
                                <Typography variant="body2" sx={{fontWeight: 500}}>
                                    {commissionProduct.commissionDescription}
                                </Typography>
                                <Typography variant="body2" sx={{fontWeight: 500}}>
                                    {commissionProduct.earnedCommission}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                )}
            </Draggable>
        );
    });

    const loading = loadingCommissionCategories || loadingUnassignedCommissionProducts;

    if (loading) {
        return <Spinner/>;
    }

    return (
        <React.Fragment>
            <Header title={'Produkte und Provisionen'}
                    subtitle={'Verwaltung von Produktkategorien und Zuordnungen'}/>

            <DragDropContext onDragEnd={handleDragEnd}>
                <Container maxWidth={false} sx={{py: 4, minHeight: '100vh'}}>

                    <Grid container spacing={3} sx={{mb: 3}}>
                        {/* Left Column - Product Categories Table */}
                        <Grid item xs={12} lg={6}>
                            <Card title={'Provisionskategorien'}
                                  className={'max-h-[50vh] overflow-y-scroll flex flex-col'}>
                                {commissionCategories &&
                                    (commissionCategories.length === 0 ? (
                                            <Box sx={{p: 3, textAlign: 'center'}}>
                                                <Typography color="text.secondary">Keine Produktgruppen
                                                    vorhanden</Typography>
                                            </Box>
                                        ) : (
                                            <TableContainer>
                                                <Table sx={{
                                                    '& .MuiTableRow-root': {
                                                        '&:hover': {
                                                            backgroundColor: 'grey.50'
                                                        }
                                                    }
                                                }}>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell align="center" sx={{
                                                                fontWeight: 'bold',
                                                                color: 'primary.main'
                                                            }}>#
                                                            </TableCell>
                                                            <TableCell
                                                                sx={{
                                                                    fontWeight: 'bold',
                                                                    color: 'primary.main'
                                                                }}>Name
                                                            </TableCell>
                                                            <TableCell sx={{
                                                                fontWeight: 'bold',
                                                                color: 'primary.main'
                                                            }}>Produktgruppe
                                                            </TableCell>
                                                            <TableCell align="center" sx={{
                                                                fontWeight: 'bold',
                                                                color: 'primary.main'
                                                            }}>Punkte
                                                            </TableCell>
                                                            <TableCell align="center" sx={{
                                                                fontWeight: 'bold',
                                                                color: 'primary.main'
                                                            }}>Provision
                                                            </TableCell>
                                                            <TableCell align="center" sx={{
                                                                fontWeight: 'bold',
                                                                color: 'primary.main'
                                                            }}>Aktionen
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {commissionCategories.map((category, index) => (
                                                            <Droppable
                                                                key={category.id}
                                                                droppableId={`category-${category.id}`}
                                                                type="PRODUCT"
                                                            >
                                                                {(provided, snapshot) => (
                                                                    <TableRow
                                                                        ref={provided.innerRef}
                                                                        {...provided.droppableProps}
                                                                        onClick={() => handleCommissionCategorySelect(category.id)}
                                                                        sx={{
                                                                            backgroundColor: selectedCommissionCategory?.id === category.id ? 'success.100' : 'inherit',
                                                                            cursor: 'pointer',
                                                                            transition: 'background-color 0.2s ease',
                                                                            '&:hover': {
                                                                                backgroundColor: selectedCommissionCategory?.id === category.id ? 'success.200' : 'grey.50'
                                                                            },
                                                                            ...(snapshot.isDraggingOver && {
                                                                                backgroundColor: 'primary.100',
                                                                                borderLeft: '4px solid',
                                                                                borderColor: 'primary.main'
                                                                            })
                                                                        }}
                                                                    >
                                                                        <TableCell align="center">
                                                                            <Link component="button"
                                                                                  onClick={() => handleCommissionCategorySelect(category.id)}>
                                                                                {index + 1}
                                                                            </Link>
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <Link
                                                                                component="button"
                                                                                onClick={() => handleCommissionCategorySelect(category.id)}
                                                                                sx={{fontWeight: selectedCommissionCategory?.id === category.id ? 'bold' : 'normal'}}
                                                                            >
                                                                                {category.categoryName}
                                                                            </Link>
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <Typography variant="body2"
                                                                                        color="text.secondary">
                                                                                {category.productGroup || '-'}
                                                                            </Typography>
                                                                        </TableCell>
                                                                        <TableCell
                                                                            align="center">{category.earnedPoints}</TableCell>
                                                                        <TableCell
                                                                            align="center">{category.paidCommission}</TableCell>
                                                                        <TableCell align="center">
                                                                            <Button disabled size="small" color="error">
                                                                                Löschen
                                                                            </Button>
                                                                        </TableCell>
                                                                        <Box sx={{display: 'none'}}>
                                                                            {provided.placeholder}
                                                                        </Box>
                                                                    </TableRow>
                                                                )}
                                                            </Droppable>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        )
                                    )}
                            </Card>
                        </Grid>

                        {/* Right Column - Unassigned Products */}
                        <Grid item xs={12} lg={6}>
                            {unassignedCommissionProducts && (
                                <Card
                                    title={`Nicht zugeordnete Produkte (${unassignedCommissionProducts.length})`}
                                    description={'Ziehen Sie Produkte in den "Zugeordnete Produkte" Bereich, um sie zuzuordnen'}
                                    className={'max-h-[50vh] overflow-y-scroll flex flex-col'}>

                                    <CardContent>
                                        <Droppable droppableId="unassigned" type="PRODUCT">
                                            {(provided, snapshot) => (
                                                <Box
                                                    ref={provided.innerRef}
                                                    {...provided.droppableProps}
                                                    sx={{
                                                        minHeight: 300,
                                                        p: 2,
                                                        m: 2,
                                                        border: '2px dashed',
                                                        borderColor: snapshot.isDraggingOver ? 'primary.main' : 'grey.300',
                                                        borderRadius: 2,
                                                        backgroundColor: snapshot.isDraggingOver ? 'primary.50' : 'transparent',
                                                        transition: 'all 0.3s ease'
                                                    }}
                                                >
                                                    {unassignedCommissionProducts.length === 0 ? (
                                                        <Box sx={{
                                                            textAlign: 'center',
                                                            py: 4,
                                                            color: 'text.secondary'
                                                        }}>
                                                            <Typography variant="h6" gutterBottom>
                                                                Alle Produkte sind zugeordnet
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                Ziehen Sie Produkte aus dem unteren Bereich hierher, um
                                                                sie zu entfernen
                                                            </Typography>
                                                        </Box>
                                                    ) : (
                                                        unassignedCommissionProducts.map((commissionProduct, index) => (
                                                            <CommissionProductCard
                                                                key={commissionProduct.id}
                                                                commissionProduct={commissionProduct}
                                                                index={index}
                                                            />
                                                        ))
                                                    )}
                                                    {provided.placeholder}
                                                </Box>
                                            )}
                                        </Droppable>
                                    </CardContent>
                                </Card>
                            )}
                        </Grid>
                    </Grid>

                    {/* Bottom Section - Assigned Products */}
                    <Grid container spacing={3} sx={{width: '100%'}}>
                        <Grid item xs={12}>
                            {selectedCommissionCategory ? (
                                <Card
                                    title={`${selectedCommissionCategory.categoryName}: Zugeordnete Produkte (${selectedCommissionCategory.commissionProducts.length})`}>
                                    <CardContent>
                                        <Droppable droppableId="assigned" type="PRODUCT">
                                            {(provided, snapshot) => (
                                                <Box
                                                    ref={provided.innerRef}
                                                    {...provided.droppableProps}
                                                    sx={{
                                                        minHeight: 300,
                                                        p: 2,
                                                        m: 2,
                                                        border: '2px dashed',
                                                        borderRadius: 2,
                                                        borderColor: snapshot.isDraggingOver ? 'success.main' : 'grey.300',
                                                        backgroundColor: snapshot.isDraggingOver ? 'success.50' : 'transparent',
                                                        transition: 'all 0.3s ease'
                                                    }}
                                                >
                                                    {selectedCommissionCategory?.commissionProducts?.length === 0 ? (
                                                        <Box sx={{
                                                            textAlign: 'center',
                                                            py: 4,
                                                            color: 'text.secondary'
                                                        }}>
                                                            <Typography variant="h6" gutterBottom>
                                                                Keine Produkte zugeordnet
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                Ziehen Sie Produkte aus dem oberen Bereich hierher, um sie
                                                                zuzuordnen
                                                            </Typography>
                                                        </Box>
                                                    ) : (
                                                        selectedCommissionCategory?.commissionProducts.map((commissionProduct, index) => (
                                                            <CommissionProductCard
                                                                key={commissionProduct.id}
                                                                commissionProduct={commissionProduct}
                                                                index={index}
                                                            />
                                                        ))
                                                    )}
                                                    {provided.placeholder}
                                                </Box>
                                            )}
                                        </Droppable>
                                    </CardContent>
                                </Card>
                            ) : (
                                <Typography>
                                    Bitte wählen Sie zunächst eine Produktkategorie aus, um die zugeordneten Produkte zu
                                    sehen
                                </Typography>
                            )}
                        </Grid>
                    </Grid>
                </Container>
            </DragDropContext>
        </React.Fragment>
    );
};

export default CommissionManager;